import React from "react";
import Image from "next/image";
import { CheckCircleFilled } from "@ant-design/icons";
import {
  FinancialBrand,
  SuccessStepAssets,
} from "@dreambigger/shared/src/types";
import { optimizeCloudinaryImg } from "@dreambigger/shared/src/utils";
import { getIcon } from "@dreambigger/design-system/src/utils";
import animations from "@dreambigger/shared/src/components/board/styles/animations.module.scss";
import Confetti from "react-confetti";
import { useSegment } from "@dreambigger/shared/src/hooks";

interface SuccessContentV2Props {
  brand: FinancialBrand;
  title: string;
  subTitle?: string;
  description: string;
  assets?: SuccessStepAssets;
  heroImage?: string;
  children?: React.ReactNode;
  isAdminMode: boolean;
}

const SuccessContentV2 = ({
  brand,
  title,
  subTitle,
  description,
  assets,
  heroImage,
  children,
  isAdminMode,
}: SuccessContentV2Props) => {
  const maxWidth = "1800px";
  const segment = useSegment();

  const trackAnalytics = () => {
    segment.track({ action: "Logo Click", label: "Logo - Success" });
  };

  return (
    <>
      {!isAdminMode && !assets?.disableConfetti && (
        <Confetti recycle={false} numberOfPieces={900} tweenDuration={12000} />
      )}
      <div
        className="min-h-100vh"
        style={{
          backgroundColor: "white",
        }}
      >
        {/* Header */}
        <div
          className="pa-3 xl_pt-4 xl_ph-6 xxl_ph-8 mb-4 lg_mb-8"
          style={{
            maxWidth,
            margin: "0 auto",
          }}
        >
          <div
            className="flex items-center pb-1"
            style={{
              gap: "12px",
              borderBottom: "1px solid #d1d5db",
            }}
          >
            {brand.primaryLogo &&
              (brand.primaryLogoUrl ? (
                <a
                  href={brand.primaryLogoUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={trackAnalytics}
                >
                  <img
                    src={optimizeCloudinaryImg(brand.primaryLogo)}
                    alt="Logo"
                    style={{
                      height:
                        brand.acquireLogoHeight ||
                        brand.primaryLogoHeight ||
                        "24px",
                      width: "auto",
                      objectFit: "contain",
                    }}
                  />
                </a>
              ) : (
                <img
                  src={optimizeCloudinaryImg(brand.primaryLogo)}
                  alt="Logo"
                  style={{
                    height:
                      brand.acquireLogoHeight ||
                      brand.primaryLogoHeight ||
                      "24px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="ph-5 pv-3 md_ph-3 md_pv-3 xl_ph-6 xxl_ph-8 min">
          <div
            className={`${animations.slideInUp_f} flex flex-column md_flex-row md_items-center md_justify-center`}
            style={{
              maxWidth,
              margin: "0 auto",
              gap: "48px",
            }}
          >
            {/* Left Content */}
            <div
              className="flex flex-column justify-center"
              style={{ maxWidth: "600px" }}
            >
              {/* Success Icon */}
              <div className="flex justify-start mb-4">
                {assets?.icon ? (
                  <div
                    className={`${!assets.iconColor && "primary"}`}
                    style={{ color: assets.iconColor }}
                  >
                    {getIcon(assets.icon, 6)}
                  </div>
                ) : (
                  <CheckCircleFilled
                    style={{
                      color: brand.primaryColor,
                      fontSize: "64px",
                    }}
                  />
                )}
              </div>

              {/* Title */}
              <h1
                className={`fwt lh-1 f-7 lg_f-9 ${animations.fadeIn_f}`}
                style={{
                  color: brand.stepTitleColor || brand.primaryColor,
                }}
                dangerouslySetInnerHTML={{
                  __html: title ?? "",
                }}
              />

              {/* Subtitle */}
              {subTitle && (
                <h2
                  className={`fwb lh-1 f-5 lg_f-7 mt-3 ${animations.fadeIn_m}`}
                  style={{
                    color: brand.stepTitleColor || brand.primaryColor,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: subTitle,
                  }}
                />
              )}

              {/* Description */}
              <div
                className={`f-4 lg_f-5 lh-2 lg_mt-6 ${animations.fadeIn_s}`}
                style={{
                  color: "#6b7280",
                }}
                dangerouslySetInnerHTML={{
                  __html: description ?? "",
                }}
              />

              {/* Children (any additional content) */}
              {children}
            </div>

            {/* Right Image - Hidden on mobile */}
            {heroImage && (
              <div
                className="relative dn md_db"
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  height: "500px",
                  flexShrink: 0,
                }}
              >
                <Image
                  src={optimizeCloudinaryImg(heroImage)}
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center"
                  priority={true}
                  alt="Success image"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessContentV2;
